<script>
import {
  Editor, EditorContent, Extension, Node,
} from '@tiptap/vue-3';
import StarterKit from '@tiptap/starter-kit';
import Underline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import Link from '@tiptap/extension-link';
import { TextStyle } from '@tiptap/extension-text-style';
import FontFamily from '@tiptap/extension-font-family';
import Color from '@tiptap/extension-color';
import Highlight from '@tiptap/extension-highlight';
import Subscript from '@tiptap/extension-subscript';
import Superscript from '@tiptap/extension-superscript';
import { Table } from '@tiptap/extension-table';
import TableRow from '@tiptap/extension-table-row';
import TableCell from '@tiptap/extension-table-cell';
import TableHeader from '@tiptap/extension-table-header';
import TaskList from '@tiptap/extension-task-list';
import TaskItem from '@tiptap/extension-task-item';
import CharacterCount from '@tiptap/extension-character-count';
import Heading from '@tiptap/extension-heading';
import Youtube from '@tiptap/extension-youtube';
// eslint-disable-next-line import/no-extraneous-dependencies, import/extensions
import { Plugin, PluginKey } from 'prosemirror-state';
import {
  FONT_FAMILIES,
  FONT_SIZES,
  LINE_HEIGHTS,
  EMOJI_LIST,
  CHAR_MAP,
} from './constants';
import {
  isGoogleDocsContent,
  cleanGoogleDocsHtml,
  extractAndUploadImages,
  hasBase64Images,
  replaceBase64Images,
} from './clipboard-helpers';
import CustomImage from './extensions/CustomImage';
import RawHtmlBlock from './extensions/RawHtmlBlock';

// FontSize extension — adds fontSize command to TextStyle
const FontSize = Extension.create({
  name: 'fontSize',
  addGlobalAttributes() {
    return [{
      types: ['textStyle'],
      attributes: {
        fontSize: {
          default: null,
          parseHTML: (el) => el.style.fontSize || null,
          renderHTML: (attrs) => {
            if (!attrs.fontSize) return {};
            return {
              style: `font-size: ${attrs.fontSize}`,
            };
          },
        },
      },
    }];
  },
  addCommands() {
    return {
      setFontSize: (size) => ({ chain }) => chain()
        .setMark('textStyle', { fontSize: size })
        .run(),
      unsetFontSize: () => ({ chain }) => chain()
        .setMark('textStyle', { fontSize: null })
        .removeEmptyTextStyle()
        .run(),
    };
  },
});

// LineHeight extension — adds lineHeight to paragraph and heading
const LineHeight = Extension.create({
  name: 'lineHeight',
  addGlobalAttributes() {
    return [{
      types: ['paragraph', 'heading'],
      attributes: {
        lineHeight: {
          default: null,
          parseHTML: (el) => el.style.lineHeight || null,
          renderHTML: (attrs) => {
            if (!attrs.lineHeight) return {};
            return {
              style: `line-height: ${attrs.lineHeight}`,
            };
          },
        },
      },
    }];
  },
  addCommands() {
    return {
      setLineHeight: (height) => ({ commands }) => commands
        .updateAttributes('paragraph', { lineHeight: height })
        || commands
          .updateAttributes('heading', { lineHeight: height }),
      unsetLineHeight: () => ({ commands }) => commands
        .updateAttributes('paragraph', { lineHeight: null })
        || commands
          .updateAttributes('heading', { lineHeight: null }),
    };
  },
});

// Indent extension — adds indent/outdent via margin-left
const Indent = Extension.create({
  name: 'indent',
  addGlobalAttributes() {
    return [{
      types: ['paragraph', 'heading'],
      attributes: {
        indent: {
          default: 0,
          parseHTML: (el) => {
            const ml = el.style.marginLeft;
            return ml ? parseInt(ml, 10) / 40 : 0;
          },
          renderHTML: (attrs) => {
            if (!attrs.indent || attrs.indent === 0) return {};
            return {
              style: `margin-left: ${attrs.indent * 40}px`,
            };
          },
        },
      },
    }];
  },
  addCommands() {
    return {
      indent: () => ({ tr, state, dispatch }) => {
        const { selection } = state;
        const pos = selection.$from;
        const node = pos.node(pos.depth);
        if (!node) return false;
        const currentIndent = node.attrs.indent || 0;
        if (currentIndent >= 10) return false;
        if (dispatch) {
          tr.setNodeMarkup(
            pos.before(pos.depth),
            undefined,
            { ...node.attrs, indent: currentIndent + 1 },
          );
          dispatch(tr);
        }
        return true;
      },
      outdent: () => ({ tr, state, dispatch }) => {
        const { selection } = state;
        const pos = selection.$from;
        const node = pos.node(pos.depth);
        if (!node) return false;
        const currentIndent = node.attrs.indent || 0;
        if (currentIndent <= 0) return false;
        if (dispatch) {
          tr.setNodeMarkup(
            pos.before(pos.depth),
            undefined,
            { ...node.attrs, indent: currentIndent - 1 },
          );
          dispatch(tr);
        }
        return true;
      },
    };
  },
});

// Custom Heading extension that preserves 'class' and 'id' attributes
// (needed for 'anchor' class and anchor IDs from imported HTML)
const CustomHeading = Heading.extend({
  addAttributes() {
    return {
      ...this.parent?.(),
      class: {
        default: null,
        parseHTML: (element) => element.getAttribute('class') || null,
        renderHTML: (attributes) => {
          if (!attributes.class) return {};
          return { class: attributes.class };
        },
      },
      id: {
        default: null,
        parseHTML: (element) => element.getAttribute('id') || null,
        renderHTML: (attributes) => {
          if (!attributes.id) return {};
          return { id: attributes.id };
        },
      },
    };
  },
});

// Comparison Slider node — atomic block for before/after image comparisons
const ComparisonSliderNode = Node.create({
  name: 'comparisonSlider',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      imageBeforeUri: { default: null },
      imageAfterUri: { default: null },
    };
  },

  parseHTML() {
    return [{
      tag: 'div.comparison-slide-card',
      getAttrs: (dom) => {
        const imgs = dom.querySelectorAll('img');
        return {
          imageBeforeUri: imgs[0]?.getAttribute('src') || null,
          imageAfterUri: imgs[1]?.getAttribute('src') || null,
        };
      },
    }];
  },

  renderHTML({ HTMLAttributes }) {
    const before = HTMLAttributes.imageBeforeUri || '';
    const after = HTMLAttributes.imageAfterUri || '';
    return [
      'div', { class: 'comparison-slide-card' },
      [
        'div',
        { class: 'comparison-slide-card__inner-slide' },
        [
          'img',
          {
            class: 'comparison-slide-card__inner-slide',
            alt: 'image-before',
            src: before,
          },
        ],
      ],
      [
        'div',
        {
          class: 'comparison-slide-card__inner-comparison-slide'
            + ' comparison-slide-card'
            + '__inner-comparison-slide_after',
        },
        [
          'img',
          {
            class: 'comparison-slide-card__inner-slide',
            alt: 'image-after',
            src: after,
          },
        ],
      ],
      [
        'div',
        { class: 'comparison-slide-card__comparison-btn' },
      ],
    ];
  },
});

// Ad Block node — atomic block for in-article advertisements
const AdBlockNode = Node.create({
  name: 'adBlock',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes() {
    return {
      imageDesktopUri: { default: null },
      imageAlt: { default: '' },
      title: { default: '' },
      description: { default: '' },
      buttonText: { default: '' },
      buttonLink: { default: '' },
    };
  },

  parseHTML() {
    return [{
      tag: 'div.article-in-ad-block',
      getAttrs: (dom) => {
        const img = dom.querySelector(
          '.article-in-ad-block-img',
        );
        const titleEl = dom.querySelector(
          '.article-in-ad-block-title',
        );
        const descEl = dom.querySelector(
          '.article-in-ad-block-description',
        );
        const btnEl = dom.querySelector(
          '.article-in-ad-block-btn',
        );
        return {
          imageDesktopUri: img?.getAttribute('src') || null,
          imageAlt: img?.getAttribute('alt') || '',
          title: titleEl?.textContent || '',
          description: descEl?.textContent || '',
          buttonText: btnEl?.textContent || '',
          buttonLink: btnEl?.getAttribute('href') || '',
        };
      },
    }];
  },

  renderHTML({ HTMLAttributes }) {
    const {
      imageDesktopUri,
      imageAlt,
      title,
      description,
      buttonText,
      buttonLink,
    } = HTMLAttributes;
    return [
      'div', { class: 'article-in-ad-block' },
      [
        'img',
        {
          class: 'article-in-ad-block-img',
          src: imageDesktopUri || '',
          alt: imageAlt || '',
        },
      ],
      [
        'div',
        { class: 'article-in-ad-block-data' },
        [
          'h2',
          { class: 'article-in-ad-block-title' },
          title || '',
        ],
        [
          'span',
          { class: 'article-in-ad-block-description' },
          description || '',
        ],
        [
          'a',
          {
            href: buttonLink || '',
            class: 'article-in-ad-block-btn',
          },
          buttonText || '',
        ],
      ],
    ];
  },
});

// ClipboardHandler extension — intercepts paste events to handle Google Docs content
// and re-upload images to S3 instead of keeping temporary Google URLs
const ClipboardHandler = Extension.create({
  name: 'clipboardHandler',

  addOptions() {
    return {
      imageUploadHandler: null,
    };
  },

  addProseMirrorPlugins() {
    const { imageUploadHandler } = this.options;
    const { editor } = this;

    return [
      new Plugin({
        key: new PluginKey('clipboardHandler'),
        props: {
          handlePaste(view, event) {
            const { clipboardData } = event;
            if (!clipboardData) return false;

            const html = clipboardData.getData('text/html');
            if (!html) return false;

            // Google Docs paste — clean markup and re-upload images
            if (isGoogleDocsContent(html)) {
              event.preventDefault();

              const cleanedHtml = cleanGoogleDocsHtml(html);

              // If there are images and we have an upload handler, re-upload them to S3
              const hasImages = /<img\s/i.test(cleanedHtml);
              if (hasImages && imageUploadHandler) {
                extractAndUploadImages(
                  cleanedHtml,
                  clipboardData.items,
                  imageUploadHandler,
                ).then((finalHtml) => {
                  editor.chain().focus().insertContent(finalHtml, {
                    parseOptions: { preserveWhitespace: 'full' },
                  }).run();
                });
              } else {
                editor.chain().focus().insertContent(cleanedHtml, {
                  parseOptions: { preserveWhitespace: 'full' },
                }).run();
              }

              return true;
            }

            // Any other paste with base64 images — upload them to S3
            if (hasBase64Images(html) && imageUploadHandler) {
              event.preventDefault();

              replaceBase64Images(html, imageUploadHandler).then((finalHtml) => {
                editor.chain().focus().insertContent(finalHtml, {
                  parseOptions: { preserveWhitespace: 'full' },
                }).run();
              });

              return true;
            }

            return false;
          },
        },
      }),
    ];
  },
});

export default {
  components: { EditorContent },
  emits: ['update:value'],
  props: {
    value: {
      type: String,
      default: '',
    },
    height: {
      type: Number,
      default: 700,
    },
    width: {
      type: Number,
      default: 800,
    },
    maxHeight: {
      type: Number,
      default: 1127,
    },
    customButtons: {
      type: Array,
      default: () => [],
    },
    imageUploadHandler: {
      type: Function,
      default: null,
    },
    mini: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      editor: null,
      fontFamilies: FONT_FAMILIES,
      fontSizes: FONT_SIZES,
      lineHeights: LINE_HEIGHTS,
      emojiList: EMOJI_LIST,
      charMap: CHAR_MAP,
      selectedFontFamily: '',
      selectedFontSize: '',
      selectedLineHeight: '',
      selectedTextColor: '#ffffff',
      selectedBgColor: '',
      showColorPicker: false,
      showBgColorPicker: false,
      showLinkInput: false,
      linkUrl: '',
      showImageUrlInput: false,
      imageUrl: '',
      showYoutubeInput: false,
      youtubeUrl: '',
      showEmojiPicker: false,
      showCharMap: false,
      showSearchReplace: false,
      searchText: '',
      replaceText: '',
      showTableMenu: false,
      isFullscreen: false,
      isImportingHtml: false,
      // Import HTML modal state
      showHtmlImportModal: false,
      htmlImportContent: '',
      // View Source toggle state
      isSourceMode: false,
      sourceContent: '',
    };
  },
  computed: {
    wordCount() {
      if (!this.editor) return 0;
      return this.editor.storage.characterCount.words();
    },
    charCount() {
      if (!this.editor) return 0;
      return this.editor.storage.characterCount.characters();
    },
  },
  watch: {
    value(newVal) {
      if (this.editor && newVal !== this.editor.getHTML()) {
        this.editor.commands.setContent(newVal, false);
      }
    },
  },
  mounted() {
    try {
      this.editor = new Editor({
        content: this.value,
        extensions: [
          StarterKit.configure({
            heading: false,
          }),
          CustomHeading.configure({
            levels: [1, 2, 3, 4, 5, 6],
          }),
          Underline,
          TextAlign.configure({
            types: ['heading', 'paragraph'],
          }),
          CustomImage.configure({
            inline: false,
            allowBase64: true,
            imageUploadHandler: this.imageUploadHandler,
          }),
          Link.configure({
            openOnClick: false,
            HTMLAttributes: {
              rel: 'noopener noreferrer',
            },
          }),
          TextStyle,
          FontFamily,
          FontSize,
          LineHeight,
          Indent,
          Color,
          Highlight.configure({
            multicolor: true,
          }),
          Subscript,
          Superscript,
          Table.configure({
            resizable: true,
          }),
          TableRow,
          TableCell,
          TableHeader,
          TaskList,
          TaskItem.configure({
            nested: true,
          }),
          CharacterCount,
          Youtube.configure({
            inline: false,
            ccLanguage: 'en',
          }),
          ComparisonSliderNode,
          AdBlockNode,
          RawHtmlBlock,
          ClipboardHandler.configure({
            imageUploadHandler: this.imageUploadHandler,
          }),
        ],
        editorProps: {
          attributes: {
            class: 'tiptap-editor-content',
            spellcheck: 'true',
          },
        },
        onUpdate: ({ editor }) => {
          // Post-process: replace RawHtmlBlock placeholder divs with actual raw HTML
          // so the API receives the original content, not the encoded wrapper
          let html = editor.getHTML();
          html = html.replace(
            /<div[^>]*class="raw-html-block"[^>]*data-raw-html="([^"]*)"[^>]*><\/div>/g,
            (_, encoded) => decodeURIComponent(encoded),
          );
          this.$emit('update:value', html);
        },
      });
    } catch (err) {
      console.error('[TiptapEditor] Init error:', err);
    }
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener(
      'click',
      this.handleOutsideClick,
    );
    if (this.editor) {
      this.editor.destroy();
    }
  },
  methods: {
    handleOutsideClick(e) {
      const wrapper = this.$el;
      if (!wrapper) return;
      const toolbar = wrapper.querySelector('.tiptap-toolbar');
      if (toolbar && !toolbar.contains(e.target)) {
        this.showColorPicker = false;
        this.showBgColorPicker = false;
        this.showTableMenu = false;
        this.showEmojiPicker = false;
        this.showCharMap = false;
      }
    },
    // Text formatting
    toggleBold() {
      this.editor.chain().focus().toggleBold().run();
    },
    toggleItalic() {
      this.editor.chain().focus().toggleItalic().run();
    },
    toggleUnderline() {
      this.editor.chain().focus().toggleUnderline().run();
    },
    toggleStrike() {
      this.editor.chain().focus().toggleStrike().run();
    },
    toggleSubscript() {
      this.editor.chain().focus().toggleSubscript().run();
    },
    toggleSuperscript() {
      this.editor.chain().focus().toggleSuperscript().run();
    },

    // Headings
    setHeading(level) {
      if (level === 0) {
        this.editor.chain().focus().setParagraph().run();
      } else {
        this.editor.chain().focus().toggleHeading({ level }).run();
      }
    },

    // Lists
    toggleBulletList() {
      this.editor.chain().focus().toggleBulletList().run();
    },
    toggleOrderedList() {
      this.editor.chain().focus().toggleOrderedList().run();
    },
    toggleTaskList() {
      this.editor.chain().focus().toggleTaskList().run();
    },

    // Alignment
    setAlign(alignment) {
      this.editor.chain().focus().setTextAlign(alignment).run();
    },

    // Font
    setFontFamily(font) {
      this.selectedFontFamily = font;
      if (font) {
        this.editor.chain().focus().setFontFamily(font).run();
      } else {
        this.editor.chain().focus().unsetFontFamily().run();
      }
    },
    setFontSize(size) {
      this.selectedFontSize = size;
      if (size) {
        this.editor.commands.setFontSize(size);
      } else {
        this.editor.commands.unsetFontSize();
      }
    },

    // Line Height
    setLineHeight(height) {
      this.selectedLineHeight = height;
      if (height) {
        this.editor.commands.setLineHeight(height);
      } else {
        this.editor.commands.unsetLineHeight();
      }
    },

    // Indent / Outdent
    indentContent() {
      this.editor.commands.indent();
    },
    outdentContent() {
      this.editor.commands.outdent();
    },

    // Colors
    setTextColor(color) {
      this.selectedTextColor = color;
      this.editor.chain().focus().setColor(color).run();
      this.showColorPicker = false;
    },
    setBgColor(color) {
      this.selectedBgColor = color;
      this.editor.chain().focus().toggleHighlight({ color }).run();
      this.showBgColorPicker = false;
    },

    // Links
    openLinkInput() {
      const previousUrl = this.editor.getAttributes('link').href;
      this.linkUrl = previousUrl || '';
      this.showLinkInput = true;
    },
    setLink() {
      if (this.linkUrl === '') {
        this.editor.chain().focus()
          .extendMarkRange('link')
          .unsetLink()
          .run();
      } else {
        this.editor.chain().focus()
          .extendMarkRange('link')
          .setLink({ href: this.linkUrl })
          .run();
      }
      this.showLinkInput = false;
      this.linkUrl = '';
    },
    removeLink() {
      this.editor.chain().focus()
        .extendMarkRange('link')
        .unsetLink()
        .run();
      this.showLinkInput = false;
    },

    // Images
    openImageUpload() {
      if (this.imageUploadHandler) {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = 'image/*';
        input.onchange = async (e) => {
          const file = e.target.files[0];
          if (file) {
            const blobFn = () => file;
            const filenameFn = () => file.name;
            const fileObj = { blob: blobFn, filename: filenameFn };
            await this.imageUploadHandler(fileObj, (resultUrl) => {
              this.editor.chain().focus()
                .setImage({ src: resultUrl }).run();
            });
          }
        };
        input.click();
      } else {
        this.showImageUrlInput = true;
      }
    },
    insertImageByUrl() {
      if (this.imageUrl) {
        this.editor.chain().focus().setImage({ src: this.imageUrl }).run();
      }
      this.showImageUrlInput = false;
      this.imageUrl = '';
    },

    // Tables
    insertTable() {
      this.editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run();
    },
    addColumnBefore() {
      this.editor.chain().focus().addColumnBefore().run();
    },
    addColumnAfter() {
      this.editor.chain().focus().addColumnAfter().run();
    },
    addRowBefore() {
      this.editor.chain().focus().addRowBefore().run();
    },
    addRowAfter() {
      this.editor.chain().focus().addRowAfter().run();
    },
    deleteColumn() {
      this.editor.chain().focus().deleteColumn().run();
    },
    deleteRow() {
      this.editor.chain().focus().deleteRow().run();
    },
    deleteTable() {
      this.editor.chain().focus().deleteTable().run();
    },
    mergeCells() {
      this.editor.chain().focus().mergeCells().run();
    },
    splitCell() {
      this.editor.chain().focus().splitCell().run();
    },

    // Misc
    toggleBlockquote() {
      this.editor.chain().focus().toggleBlockquote().run();
    },
    setHorizontalRule() {
      this.editor.chain().focus().setHorizontalRule().run();
    },
    toggleCode() {
      this.editor.chain().focus().toggleCode().run();
    },
    toggleCodeBlock() {
      this.editor.chain().focus().toggleCodeBlock().run();
    },
    undo() {
      this.editor.chain().focus().undo().run();
    },
    redo() {
      this.editor.chain().focus().redo().run();
    },
    clearFormat() {
      this.editor.chain().focus()
        .clearNodes()
        .unsetAllMarks()
        .run();
    },
    toggleFullscreen() {
      this.isFullscreen = !this.isFullscreen;
    },
    // Toggles between WYSIWYG editor and raw HTML source editing.
    // Source mode shows the full HTML including RawHtmlBlock content
    // and allows direct editing that's applied back on toggle.
    viewSource() {
      if (this.isSourceMode) {
        // Exiting source mode — apply changes back to editor
        this.editor.commands.setContent(this.sourceContent, false);
        this.$emit('update:value', this.editor.getHTML());
        this.isSourceMode = false;
      } else {
        // Entering source mode — get current HTML with raw blocks decoded
        let html = this.editor.getHTML();
        // Decode RawHtmlBlock placeholders for readable source view
        html = html.replace(
          /<div[^>]*class="raw-html-block"[^>]*data-raw-html="([^"]*)"[^>]*><\/div>/g,
          (_, encoded) => decodeURIComponent(encoded),
        );
        this.sourceContent = html.replace(/></g, '>\n<').replace(/\n\n/g, '\n');
        this.isSourceMode = true;
      }
    },

    // Emoji
    insertEmoji(emoji) {
      this.editor.chain().focus().insertContent(emoji).run();
      this.showEmojiPicker = false;
    },

    // Character Map
    insertChar(char) {
      this.editor.chain().focus().insertContent(char).run();
      this.showCharMap = false;
    },

    // YouTube / Media Embed
    openYoutubeInput() {
      this.youtubeUrl = '';
      this.showYoutubeInput = true;
    },
    insertYoutube() {
      if (this.youtubeUrl) {
        this.editor.commands.setYoutubeVideo({
          src: this.youtubeUrl,
        });
      }
      this.showYoutubeInput = false;
      this.youtubeUrl = '';
    },

    // Search & Replace
    openSearchReplace() {
      this.showSearchReplace = !this.showSearchReplace;
      this.searchText = '';
      this.replaceText = '';
    },
    findNext() {
      if (!this.searchText) return;
      const { state } = this.editor;
      const { doc } = state;
      const text = doc.textContent;
      const cursorPos = state.selection.from;
      const afterCursor = text.substring(cursorPos);
      let idx = afterCursor
        .toLowerCase()
        .indexOf(this.searchText.toLowerCase());
      if (idx === -1) {
        idx = text
          .toLowerCase()
          .indexOf(this.searchText.toLowerCase());
        if (idx === -1) return;
      } else {
        idx += cursorPos;
      }
      // Map text position to document position
      let pos = 0;
      let found = false;
      doc.descendants((node, nodePos) => {
        if (found) return false;
        if (node.isText) {
          const nodeText = node.text.toLowerCase();
          const sIdx = nodeText.indexOf(
            this.searchText.toLowerCase(),
            nodePos <= idx ? idx - pos : 0,
          );
          if (sIdx !== -1 && pos + sIdx >= idx) {
            const from = nodePos + sIdx;
            const to = from + this.searchText.length;
            this.editor.chain().focus()
              .setTextSelection({ from, to })
              .run();
            found = true;
          }
          pos += node.text.length;
        }
        return !found;
      });
    },
    replaceNext() {
      if (!this.searchText) return;
      const { from, to } = this.editor.state.selection;
      const selected = this.editor.state.doc
        .textBetween(from, to);
      if (
        selected.toLowerCase()
        === this.searchText.toLowerCase()
      ) {
        this.editor.chain().focus()
          .insertContentAt({ from, to }, this.replaceText)
          .run();
      }
      this.findNext();
    },
    replaceAll() {
      if (!this.searchText) return;
      const { doc } = this.editor.state;
      const positions = [];
      doc.descendants((node, pos) => {
        if (node.isText) {
          const { text } = node;
          let idx = text
            .toLowerCase()
            .indexOf(this.searchText.toLowerCase());
          while (idx !== -1) {
            positions.push({
              from: pos + idx,
              to: pos + idx + this.searchText.length,
            });
            idx = text.toLowerCase().indexOf(
              this.searchText.toLowerCase(),
              idx + 1,
            );
          }
        }
      });
      // Replace from end to start to keep positions valid
      const reversed = positions.reverse();
      reversed.forEach(({ from, to }) => {
        this.editor.chain().focus()
          .insertContentAt({ from, to }, this.replaceText)
          .run();
      });
    },

    // Insert raw HTML (for custom blocks)
    insertContent(html) {
      this.editor.chain().focus().insertContent(html, {
        parseOptions: { preserveWhitespace: 'full' },
      }).run();
    },

    // Import HTML — opens modal with textarea for pasting raw HTML.
    // No sanitization applied — custom styled divs, review cards, etc. are preserved
    // as RawHtmlBlock nodes by the catch-all extension.
    openHtmlImport() {
      this.htmlImportContent = '';
      this.showHtmlImportModal = true;
    },

    async confirmHtmlImport() {
      if (!this.htmlImportContent.trim()) return;

      this.isImportingHtml = true;
      try {
        let html = this.htmlImportContent;

        // Upload base64 images to S3 if present — only processing step we keep
        if (this.imageUploadHandler && hasBase64Images(html)) {
          html = await replaceBase64Images(html, this.imageUploadHandler);
        }

        this.editor.commands.setContent(html, false);
        this.$emit('update:value', this.editor.getHTML());
        this.showHtmlImportModal = false;
      } catch (err) {
        console.error('[TiptapEditor] HTML import error:', err);
      } finally {
        this.isImportingHtml = false;
      }
    },

    // Get editor instance (for custom button integrations)
    getEditor() {
      return this.editor;
    },
  },
};
</script>

<template>
  <div
    :class="['tiptap-wrapper', {
      'tiptap-wrapper--fullscreen': isFullscreen,
      'tiptap-wrapper--mini': mini,
    }]"
    :style="{
      width: isFullscreen ? '100%' : width + 'px',
      maxHeight: isFullscreen ? '100vh' : maxHeight + 'px',
      height: isFullscreen ? '100vh' : height + 'px',
    }"
  >
    <!-- Toolbar -->
    <div class="tiptap-toolbar" v-if="editor">
      <!-- Row 1: History, Text formatting, Lists -->
      <div class="tiptap-toolbar__row">
        <!-- Undo / Redo -->
        <div class="tiptap-toolbar__group">
          <button
            class="tiptap-toolbar__btn"
            @click="undo"
            title="Undo"
          >&#8630;</button>
          <button
            class="tiptap-toolbar__btn"
            @click="redo"
            title="Redo"
          >&#8631;</button>
        </div>

        <div class="tiptap-toolbar__separator"></div>

        <!-- Bold, Italic, Underline, Strikethrough -->
        <div class="tiptap-toolbar__group">
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('bold') }"
            @click="toggleBold"
            title="Bold"
          ><b>B</b></button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('italic') }"
            @click="toggleItalic"
            title="Italic"
          ><i>I</i></button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('underline') }"
            @click="toggleUnderline"
            title="Underline"
          ><u>U</u></button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('strike') }"
            @click="toggleStrike"
            title="Strikethrough"
          ><s>S</s></button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('subscript') }"
            @click="toggleSubscript"
            title="Subscript"
          >X<sub>2</sub></button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('superscript') }"
            @click="toggleSuperscript"
            title="Superscript"
          >X<sup>2</sup></button>
        </div>

        <div class="tiptap-toolbar__separator"></div>

        <!-- Alignment -->
        <div class="tiptap-toolbar__group">
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive({ textAlign: 'left' }) }"
            @click="setAlign('left')"
            title="Align Left"
          >&#8676;</button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive({ textAlign: 'center' }) }"
            @click="setAlign('center')"
            title="Align Center"
          >&#8596;</button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive({ textAlign: 'right' }) }"
            @click="setAlign('right')"
            title="Align Right"
          >&#8677;</button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive({ textAlign: 'justify' }) }"
            @click="setAlign('justify')"
            title="Justify"
          >&#8700;</button>
        </div>

        <div class="tiptap-toolbar__separator"></div>

        <!-- Lists -->
        <div class="tiptap-toolbar__group">
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('taskList') }"
            @click="toggleTaskList"
            title="Checklist"
          >&#9745;</button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('orderedList') }"
            @click="toggleOrderedList"
            title="Ordered List"
          >1.</button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('bulletList') }"
            @click="toggleBulletList"
            title="Bullet List"
          >&#8226;</button>
        </div>
      </div>

      <!-- Row 2: Font, Size, Headings, Colors -->
      <div class="tiptap-toolbar__row" v-if="!mini">
        <!-- Font Family -->
        <div class="tiptap-toolbar__group">
          <select
            class="tiptap-toolbar__select"
            :value="selectedFontFamily"
            @change="setFontFamily($event.target.value)"
            title="Font Family"
            aria-label="Font Family"
          >
            <option value="">Font</option>
            <option
              v-for="font in fontFamilies"
              :key="font.value"
              :value="font.value"
              :style="{ fontFamily: font.value }"
            >{{ font.label }}</option>
          </select>
        </div>

        <!-- Font Size -->
        <div class="tiptap-toolbar__group">
          <select
            class="tiptap-toolbar__select tiptap-toolbar__select--sm"
            :value="selectedFontSize"
            @change="setFontSize($event.target.value)"
            title="Font Size"
            aria-label="Font Size"
          >
            <option value="">Size</option>
            <option
              v-for="size in fontSizes"
              :key="size"
              :value="size"
            >{{ size }}</option>
          </select>
        </div>

        <!-- Headings -->
        <div class="tiptap-toolbar__group">
          <select
            class="tiptap-toolbar__select"
            @change="setHeading(Number($event.target.value))"
            title="Format"
            aria-label="Format"
          >
            <option value="0">Paragraph</option>
            <option value="1">Heading 1</option>
            <option value="2">Heading 2</option>
            <option value="3">Heading 3</option>
            <option value="4">Heading 4</option>
            <option value="5">Heading 5</option>
            <option value="6">Heading 6</option>
          </select>
        </div>

        <!-- Line Height -->
        <div class="tiptap-toolbar__group">
          <select
            class="tiptap-toolbar__select tiptap-toolbar__select--sm"
            :value="selectedLineHeight"
            @change="setLineHeight($event.target.value)"
            title="Line Height"
            aria-label="Line Height"
          >
            <option value="">LH</option>
            <option
              v-for="lh in lineHeights"
              :key="lh"
              :value="lh"
            >{{ lh }}</option>
          </select>
        </div>

        <div class="tiptap-toolbar__separator"></div>

        <!-- Link -->
        <div class="tiptap-toolbar__group">
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': editor.isActive('link') }"
            @click="openLinkInput"
            title="Insert Link"
          >&#128279;</button>
        </div>

        <div class="tiptap-toolbar__separator"></div>

        <!-- Image -->
        <div class="tiptap-toolbar__group">
          <button
            class="tiptap-toolbar__btn"
            @click="openImageUpload"
            title="Insert Image"
          >&#128247;</button>
        </div>

        <!-- Table -->
        <div class="tiptap-toolbar__group tiptap-toolbar__group--color">
          <button
            class="tiptap-toolbar__btn"
            @click="showTableMenu = !showTableMenu"
            title="Table"
          >&#9638;</button>
          <div
            v-if="showTableMenu"
            class="tiptap-toolbar__dropdown"
          >
            <button
              class="tiptap-toolbar__dropdown-item"
              @click="insertTable(); showTableMenu = false"
            >Insert Table</button>
            <button
              class="tiptap-toolbar__dropdown-item"
              @click="addRowBefore(); showTableMenu = false"
            >Add Row Before</button>
            <button
              class="tiptap-toolbar__dropdown-item"
              @click="addRowAfter(); showTableMenu = false"
            >Add Row After</button>
            <button
              class="tiptap-toolbar__dropdown-item"
              @click="addColumnBefore(); showTableMenu = false"
            >Add Column Before</button>
            <button
              class="tiptap-toolbar__dropdown-item"
              @click="addColumnAfter(); showTableMenu = false"
            >Add Column After</button>
            <button
              class="tiptap-toolbar__dropdown-item"
              @click="deleteRow(); showTableMenu = false"
            >Delete Row</button>
            <button
              class="tiptap-toolbar__dropdown-item"
              @click="deleteColumn(); showTableMenu = false"
            >Delete Column</button>
            <button
              class="tiptap-toolbar__dropdown-item"
              @click="mergeCells(); showTableMenu = false"
            >Merge Cells</button>
            <button
              class="tiptap-toolbar__dropdown-item"
              @click="splitCell(); showTableMenu = false"
            >Split Cell</button>
            <button
              class="tiptap-toolbar__dropdown-item"
              @click="deleteTable(); showTableMenu = false"
            >Delete Table</button>
          </div>
        </div>

        <div class="tiptap-toolbar__separator"></div>

        <!-- Text Color -->
        <div class="tiptap-toolbar__group tiptap-toolbar__group--color">
          <button
            class="tiptap-toolbar__btn tiptap-toolbar__btn--color"
            @click="showColorPicker = !showColorPicker"
            title="Text Color"
          >
            <span>A</span>
            <span
              class="tiptap-toolbar__color-indicator"
              :style="{ backgroundColor: selectedTextColor }"
            ></span>
          </button>
          <div
            v-if="showColorPicker"
            class="tiptap-toolbar__color-dropdown"
          >
            <input
              type="color"
              :value="selectedTextColor"
              @input="setTextColor($event.target.value)"
              aria-label="Text Color"
            />
          </div>
        </div>

        <!-- Background Color -->
        <div class="tiptap-toolbar__group tiptap-toolbar__group--color">
          <button
            class="tiptap-toolbar__btn tiptap-toolbar__btn--color"
            @click="showBgColorPicker = !showBgColorPicker"
            title="Background Color"
          >
            <span style="background: yellow; padding: 0 3px;">A</span>
            <span
              class="tiptap-toolbar__color-indicator"
              :style="{
                backgroundColor: selectedBgColor || 'transparent',
              }"
            ></span>
          </button>
          <div
            v-if="showBgColorPicker"
            class="tiptap-toolbar__color-dropdown"
          >
            <input
              type="color"
              :value="selectedBgColor"
              @input="setBgColor($event.target.value)"
              aria-label="Background Color"
            />
          </div>
        </div>

        <div class="tiptap-toolbar__separator"></div>

        <!-- YouTube -->
        <div class="tiptap-toolbar__group">
          <button
            class="tiptap-toolbar__btn"
            @click="openYoutubeInput"
            title="YouTube Video"
          >&#9654;</button>
        </div>

        <div class="tiptap-toolbar__separator"></div>

        <!-- Indent / Outdent -->
        <div class="tiptap-toolbar__group">
          <button
            class="tiptap-toolbar__btn"
            @click="indentContent"
            title="Indent"
          >&#8594;</button>
          <button
            class="tiptap-toolbar__btn"
            @click="outdentContent"
            title="Outdent"
          >&#8592;</button>
        </div>

        <div class="tiptap-toolbar__separator"></div>

        <!-- Emoji / Charmap / Misc -->
        <div class="tiptap-toolbar__group">
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': showEmojiPicker }"
            @click="showEmojiPicker = !showEmojiPicker"
            title="Emoticons"
          >&#128512;</button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': showCharMap }"
            @click="showCharMap = !showCharMap"
            title="Special Characters"
          >&#937;</button>
          <button
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active':
                editor.isActive('blockquote'),
            }"
            @click="toggleBlockquote"
            title="Blockquote"
          >&#10077;</button>
          <button
            class="tiptap-toolbar__btn"
            @click="setHorizontalRule"
            title="Horizontal Rule"
          >&mdash;</button>
          <button
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active':
                editor.isActive('codeBlock'),
            }"
            @click="toggleCodeBlock"
            title="Code Block"
          >&lt;/&gt;</button>
          <button
            class="tiptap-toolbar__btn"
            @click="clearFormat"
            title="Clear Formatting"
          >T<sub>x</sub></button>
          <button
            class="tiptap-toolbar__btn"
            @click="openSearchReplace"
            title="Search & Replace"
          >&#128270;</button>
          <button
            class="tiptap-toolbar__btn"
            @click="openHtmlImport"
            :disabled="isImportingHtml"
            title="Import HTML"
          >&#128228;</button>
          <button
            class="tiptap-toolbar__btn"
            :class="{ 'tiptap-toolbar__btn--active': isSourceMode }"
            @click="viewSource"
            title="View Source"
          >&#9776;</button>
          <button
            class="tiptap-toolbar__btn"
            :class="{
              'tiptap-toolbar__btn--active': isFullscreen,
            }"
            @click="toggleFullscreen"
            title="Fullscreen"
          >&#9974;</button>
        </div>
      </div>

      <!-- Row 3: Custom buttons -->
      <div class="tiptap-toolbar__row" v-if="!mini && customButtons.length > 0">
        <div class="tiptap-toolbar__group">
          <button
            v-for="btn in customButtons"
            :key="btn.text"
            class="tiptap-toolbar__btn tiptap-toolbar__btn--custom"
            @click="btn.onAction"
            :title="btn.text"
          >{{ btn.text }}</button>
        </div>
      </div>
    </div>

    <!-- Link Input Popup -->
    <div v-if="showLinkInput" class="tiptap-popup">
      <div class="tiptap-popup__content">
        <input
          v-model="linkUrl"
          placeholder="https://..."
          class="tiptap-popup__input"
          @keydown.enter="setLink"
          aria-label="Link URL"
        />
        <button
          class="tiptap-popup__btn"
          @click="setLink"
        >Apply</button>
        <button
          class="tiptap-popup__btn tiptap-popup__btn--danger"
          @click="removeLink"
        >Remove</button>
        <button
          class="tiptap-popup__btn"
          @click="showLinkInput = false"
        >Cancel</button>
      </div>
    </div>

    <!-- Image URL Input Popup -->
    <div v-if="showImageUrlInput" class="tiptap-popup">
      <div class="tiptap-popup__content">
        <input
          v-model="imageUrl"
          placeholder="Image URL..."
          class="tiptap-popup__input"
          @keydown.enter="insertImageByUrl"
          aria-label="Image URL"
        />
        <button
          class="tiptap-popup__btn"
          @click="insertImageByUrl"
        >Insert</button>
        <button
          class="tiptap-popup__btn"
          @click="showImageUrlInput = false"
        >Cancel</button>
      </div>
    </div>

    <!-- YouTube Input Popup -->
    <div v-if="showYoutubeInput" class="tiptap-popup">
      <div class="tiptap-popup__content">
        <input
          v-model="youtubeUrl"
          placeholder="YouTube URL..."
          class="tiptap-popup__input"
          @keydown.enter="insertYoutube"
          aria-label="YouTube URL"
        />
        <button
          class="tiptap-popup__btn"
          @click="insertYoutube"
        >Insert</button>
        <button
          class="tiptap-popup__btn"
          @click="showYoutubeInput = false"
        >Cancel</button>
      </div>
    </div>

    <!-- Search & Replace Panel -->
    <div v-if="showSearchReplace" class="tiptap-popup">
      <div class="tiptap-popup__content">
        <input
          v-model="searchText"
          placeholder="Search..."
          class="tiptap-popup__input"
          @keydown.enter="findNext"
          aria-label="Search text"
        />
        <input
          v-model="replaceText"
          placeholder="Replace..."
          class="tiptap-popup__input"
          aria-label="Replace text"
        />
        <button
          class="tiptap-popup__btn"
          @click="findNext"
        >Find</button>
        <button
          class="tiptap-popup__btn"
          @click="replaceNext"
        >Replace</button>
        <button
          class="tiptap-popup__btn"
          @click="replaceAll"
        >All</button>
        <button
          class="tiptap-popup__btn"
          @click="showSearchReplace = false"
        >Close</button>
      </div>
    </div>

    <!-- Emoji Picker -->
    <div v-if="showEmojiPicker" class="tiptap-picker">
      <div class="tiptap-picker__grid">
        <button
          v-for="emoji in emojiList"
          :key="emoji"
          class="tiptap-picker__item"
          @click="insertEmoji(emoji)"
        >{{ emoji }}</button>
      </div>
    </div>

    <!-- Character Map -->
    <div v-if="showCharMap" class="tiptap-picker">
      <div class="tiptap-picker__grid">
        <button
          v-for="char in charMap"
          :key="char"
          class="tiptap-picker__item"
          @click="insertChar(char)"
        >{{ char }}</button>
      </div>
    </div>

    <!-- Editor Content / Source View toggle -->
    <div
      class="tiptap-editor-wrapper"
      :style="{
        height: isFullscreen
          ? 'calc(100vh - 140px)'
          : height + 'px',
      }"
    >
      <template v-if="!isSourceMode">
        <editor-content v-if="editor" :editor="editor" />
        <div v-else class="tiptap-editor-loading">
          Loading editor...
        </div>
      </template>
      <textarea
        v-else
        v-model="sourceContent"
        class="tiptap-source-editor"
        spellcheck="false"
        aria-label="HTML source editor"
      />
    </div>

    <!-- Status Bar -->
    <div class="tiptap-statusbar" v-if="editor && !mini">
      <span>Words: {{ wordCount }}</span>
      <span>Characters: {{ charCount }}</span>
    </div>

    <!-- Import HTML Modal -->
    <a-modal
      v-model:open="showHtmlImportModal"
      title="Import HTML"
      width="800px"
      @ok="confirmHtmlImport"
      @cancel="showHtmlImportModal = false"
      okText="Import"
      :confirmLoading="isImportingHtml"
    >
      <p style="margin-bottom: 8px; color: #8c8c8c; font-size: 13px;">
        Paste raw HTML below. Content will be inserted without sanitization.
      </p>
      <textarea
        v-model="htmlImportContent"
        class="tiptap-import-textarea"
        placeholder="<div>Your HTML here...</div>"
        spellcheck="false"
        aria-label="HTML content to import"
      />
    </a-modal>
  </div>
</template>

<style scoped lang="scss">
.tiptap-wrapper {
  border: 1px solid #3a3a3a;
  border-radius: 8px;
  overflow: hidden;
  background: #1e1e1e;
  display: flex;
  flex-direction: column;
  flex-shrink: 0;

  &--fullscreen {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw !important;
    height: 100vh;
    max-height: 100vh !important;
    z-index: 9999;
    border-radius: 0;
  }

  &--mini {
    background: #fff;
    border-color: #d9d9d9;
    border-radius: 6px;

    .tiptap-toolbar {
      background: #fafafa;
      border-bottom-color: #e8e8e8;
      padding: 2px 4px;
    }

    .tiptap-toolbar__btn {
      color: #555;

      &:hover {
        background: #e8e8e8;
        border-color: #ccc;
      }

      &--active {
        background: #e0e0e0;
        border-color: #bbb;
        color: #222;
      }
    }

    .tiptap-toolbar__separator {
      background: #ddd;
    }

    .tiptap-toolbar__select {
      background: #fff;
      border-color: #d9d9d9;
      color: #333;

      option {
        background: #fff;
        color: #333;
      }
    }

    .tiptap-editor-wrapper {
      background: #fff;
    }

    .tiptap-popup {
      background: #fafafa;
      border-bottom-color: #e8e8e8;

      .tiptap-popup__input {
        background: #fff;
        border-color: #d9d9d9;
        color: #333;

        &:focus {
          border-color: #1890ff;
        }
      }

      .tiptap-popup__btn {
        background: #f0f0f0;
        border-color: #d9d9d9;
        color: #333;

        &:hover {
          background: #e8e8e8;
        }
      }
    }
  }
}

// Mini mode deep styles for ProseMirror content
.tiptap-wrapper--mini {
  :deep(.tiptap-editor-content) {
    color: #333;
    font-size: 13px;
    padding: 12px 16px;
    line-height: 1.5;

    h1, h2, h3, h4, h5, h6 { color: #222; }

    p { margin: 0.3em 0; }

    a { color: #1890ff; }

    blockquote {
      border-left-color: #1890ff;
      color: #666;
    }

    code {
      background: #f5f5f5;
      color: #e06c75;
    }

    pre {
      background: #f5f5f5;

      code {
        color: #333;
      }
    }

    table td, table th {
      border-color: #e8e8e8;
    }

    table th {
      background: #fafafa;
    }

    hr {
      border-top-color: #e8e8e8;
    }
  }
}

.tiptap-toolbar {
  background: #2d2d2d;
  border-bottom: 1px solid #3a3a3a;
  padding: 4px;
  flex-shrink: 0;

  &__row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 2px;
    padding: 2px 0;
  }

  &__group {
    display: flex;
    align-items: center;
    gap: 1px;

    &--color {
      position: relative;
    }
  }

  &__separator {
    width: 1px;
    height: 24px;
    background: #4a4a4a;
    margin: 0 4px;
  }

  &__btn {
    background: transparent;
    border: 1px solid transparent;
    color: #ccc;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 13px;
    line-height: 1.2;
    min-width: 28px;
    height: 28px;
    display: flex;
    align-items: center;
    justify-content: center;

    &:hover {
      background: #3a3a3a;
      border-color: #555;
    }

    &--active {
      background: #4a4a4a;
      border-color: #666;
      color: #fff;
    }

    &--custom {
      font-size: 11px;
      padding: 4px 10px;
      min-width: auto;
      background: #3a3a3a;
      border-color: #555;

      &:hover {
        background: #4a4a4a;
      }
    }

    &--color {
      flex-direction: column;
      gap: 1px;
      padding: 2px 6px;
    }
  }

  &__color-indicator {
    display: block;
    width: 16px;
    height: 3px;
    border-radius: 1px;
  }

  &__color-dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 10;
    background: #2d2d2d;
    border: 1px solid #555;
    border-radius: 4px;
    padding: 6px;
    margin-top: 2px;
  }

  &__dropdown {
    position: absolute;
    top: 100%;
    left: 0;
    z-index: 10;
    background: #2d2d2d;
    border: 1px solid #555;
    border-radius: 4px;
    padding: 4px 0;
    margin-top: 2px;
    min-width: 160px;
  }

  &__dropdown-item {
    display: block;
    width: 100%;
    background: transparent;
    border: none;
    color: #ccc;
    padding: 6px 12px;
    font-size: 12px;
    text-align: left;
    cursor: pointer;
    white-space: nowrap;

    &:hover {
      background: #3a3a3a;
      color: #fff;
    }
  }

  &__select {
    background: #2d2d2d;
    border: 1px solid #4a4a4a;
    color: #ccc;
    padding: 4px 6px;
    border-radius: 4px;
    font-size: 12px;
    height: 28px;
    min-width: 100px;
    cursor: pointer;

    &--sm {
      min-width: 70px;
    }

    &:hover {
      border-color: #666;
    }

    option {
      background: #2d2d2d;
      color: #ccc;
    }
  }
}

.tiptap-editor-wrapper {
  overflow-y: auto;
  flex: 1;

  // Ensure ProseMirror root div fills the wrapper
  :deep(.ProseMirror) {
    min-height: 100%;
    outline: none;
  }
}

.tiptap-editor-loading {
  padding: 20px;
  color: #888;
  font-size: 14px;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}

// Editor content styles (deep for ProseMirror)
:deep(.tiptap-editor-content) {
  padding: 20px;
  min-height: 100%;
  outline: none;
  color: #d4d4d4;
  font-family: 'Montserrat', sans-serif;
  font-size: 14px;
  line-height: 1.6;

  > * + * {
    margin-top: 0.75em;
  }

  h1, h2, h3, h4, h5, h6 {
    color: #e0e0e0;
    line-height: 1.3;
  }

  h1 { font-size: 2em; }
  h2 { font-size: 1.5em; }
  h3 { font-size: 1.25em; }
  h4 { font-size: 1.1em; }

  p { margin: 0.5em 0; }

  a {
    color: #ecc486;
    text-decoration: underline;
    cursor: pointer;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 8px;
    display: block;
    margin: 10px 0;
  }

  blockquote {
    border-left: 3px solid #5f8aa4;
    margin: 1em 0;
    padding: 0.5em 1em;
    color: #aaa;
  }

  code {
    background: #3a3a3a;
    border-radius: 4px;
    padding: 2px 6px;
    font-family: 'Courier New', monospace;
    font-size: 0.9em;
    color: #e06c75;
  }

  pre {
    background: #1a1a1a;
    border-radius: 8px;
    padding: 16px;
    overflow-x: auto;

    code {
      background: transparent;
      padding: 0;
      color: #d4d4d4;
    }
  }

  ul, ol {
    padding-left: 1.5em;
    margin: 0.5em 0;
  }

  ul[data-type="taskList"] {
    list-style: none;
    padding-left: 0;

    li {
      display: flex;
      align-items: flex-start;
      gap: 8px;

      > label {
        flex-shrink: 0;
        margin-top: 3px;
      }

      > div {
        flex: 1;
      }
    }
  }

  table {
    border-collapse: collapse;
    margin: 1em 0;
    width: 100%;

    td, th {
      border: 1px solid #4a4a4a;
      padding: 8px 12px;
      min-width: 80px;
    }

    th {
      background: #3a3a3a;
      font-weight: 600;
    }

    .selectedCell {
      background: rgba(95, 138, 164, 0.2);
    }
  }

  hr {
    border: none;
    border-top: 1px solid #4a4a4a;
    margin: 1em 0;
  }

  // Custom block styles (comparison slider, ad block, anchor heading)
  .comparison-slide-card {
    position: relative;
    max-width: 300px;
    max-height: 300px;
    min-width: 300px;
    min-height: 300px;
    overflow: hidden;
  }

  .comparison-slide-card__inner-comparison-slide {
    position: absolute;
    width: 300px;
    height: 300px;
    object-fit: cover;
    object-position: center;
    border-radius: 17px;
    top: 0;
  }

  .comparison-slide-card__inner-comparison-slide_after {
    clip-path: inset(0 0 0 50%);
  }

  .comparison-slide-card__inner-slide {
    width: 300px;
    height: 300px;
    object-fit: cover;
    object-position: center;
    border-radius: 17px;
    max-height: 300px;
  }

  .comparison-slide-card__comparison-btn {
    position: absolute;
    outline: none;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    top: 50%;
    right: 50%;
  }

  .anchor:after {
    content: "\2693";
    display: inline-block;
    font-size: 12px;
    vertical-align: top;
  }

  .article-in-ad-block {
    display: flex;
    width: 100%;
    padding: 11px;
    justify-content: flex-start;
    align-items: center;
    background-image: linear-gradient(to right, #d7e8f0, #fff);
    border-radius: 8px;
    margin: 10px 0;
  }

  .article-in-ad-block-img {
    width: 337px;
    height: 260px;
    margin: 0 37px 0 0;
    box-shadow: 9.2px 13.1px 76px 0 rgba(0, 0, 0, 0.07);
    background-color: #7f7f7f;
    object-fit: cover;
    object-position: center;
  }

  .article-in-ad-block-data {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .article-in-ad-block-title {
    margin-bottom: 31px;
    font-size: 35px;
    font-weight: bold;
    line-height: 1;
    color: #293241;
  }

  .article-in-ad-block-description {
    margin-bottom: 21px;
    font-size: 17px;
    color: #7f7f7f;
    line-height: 1.53;
  }

  .article-in-ad-block-btn {
    width: 387px;
    height: 50px;
    box-shadow: 4px 5.7px 9.9px 3.1px rgba(95, 138, 164, 0.42);
    background-color: #5f8aa4;
    font-size: 22px;
    font-weight: 500;
    line-height: 1.2;
    text-align: center;
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    text-transform: uppercase;
    text-decoration: none;
  }
}

.tiptap-statusbar {
  background: #2d2d2d;
  border-top: 1px solid #3a3a3a;
  padding: 4px 12px;
  font-size: 11px;
  color: #888;
  display: flex;
  gap: 16px;
  flex-shrink: 0;
}

.tiptap-popup {
  background: #2d2d2d;
  border-bottom: 1px solid #3a3a3a;
  padding: 8px 12px;

  &__content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__input {
    flex: 1;
    background: #1e1e1e;
    border: 1px solid #4a4a4a;
    color: #d4d4d4;
    padding: 6px 10px;
    border-radius: 4px;
    font-size: 13px;

    &:focus {
      outline: none;
      border-color: #5f8aa4;
    }
  }

  &__btn {
    background: #3a3a3a;
    border: 1px solid #555;
    color: #ccc;
    padding: 6px 12px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;

    &:hover {
      background: #4a4a4a;
    }

    &--danger {
      color: #e06c75;
      border-color: #e06c75;

      &:hover {
        background: rgba(224, 108, 117, 0.1);
      }
    }
  }
}

.tiptap-picker {
  background: #2d2d2d;
  border-bottom: 1px solid #3a3a3a;
  padding: 8px 12px;
  max-height: 200px;
  overflow-y: auto;

  &__grid {
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
  }

  &__item {
    background: transparent;
    border: 1px solid transparent;
    color: #ccc;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 4px;
    cursor: pointer;
    font-size: 18px;
    padding: 0;

    &:hover {
      background: #3a3a3a;
      border-color: #555;
    }
  }
}

// Source view textarea — inline code editor for raw HTML
.tiptap-source-editor {
  width: 100%;
  height: 100%;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  line-height: 1.5;
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 20px;
  border: none;
  resize: none;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow: auto;
  outline: none;
}

// Import HTML modal textarea
.tiptap-import-textarea {
  width: 100%;
  height: 400px;
  font-family: 'Courier New', monospace;
  font-size: 13px;
  background: #1e1e1e;
  color: #d4d4d4;
  padding: 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  resize: vertical;
  white-space: pre-wrap;
  word-wrap: break-word;
}
</style>
